<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

import CurrencyIcon from '@/ui/CurrencyIcon.vue';
import ItemPreview from '@/garage/ItemPreview.vue';
import { boostRemaining, formatPrice } from '@/garage/format';
import type { OfferSection, SpecialOffer } from '@/state/special-offers';

/**
 * One featured offer, put in front of the player on sign-in and again every
 * 35–90 minutes (`state/special-offers.ts` decides which offer and when).
 *
 * Three ways out, in the order a player is likely to want them:
 *
 * - **Open shop** — goes to the shelf the offer lives on.
 * - **Buy now** — only for what is bought with diamonds (a bundle, a boost). It
 *   does *not* spend: it moves to a second step that asks *Are you sure you want
 *   to spend X diamonds on Y?*, with **No** and a highlighted **Yes — X 💎**.
 *   The spend itself is the garage's own purchase call, so the server's checks
 *   (price, ownership, balance, the quote pinned by a limited offer's id) are
 *   exactly the shop's.
 * - **Maybe later** — closes it; the next one is already scheduled.
 *
 * A diamond pack is bought with money, so it only ever leads to the shop.
 *
 * This is a courtesy, never a gate — it adds no way to buy anything the shop
 * does not already sell.
 */
const props = defineProps<{
  offer: SpecialOffer;
  /** The player's diamonds, to disable a purchase they cannot afford. */
  diamonds: number;
  busy?: boolean;
}>();

const emit = defineEmits<{
  close: [];
  view: [section: OfferSection];
  buy: [offer: SpecialOffer];
}>();

const step = ref<'offer' | 'confirm'>('offer');
const imageFailed = ref(false);

const cost = computed(() => props.offer.diamondCost ?? 0);
const canAfford = computed(() => props.diamonds >= cost.value);

/** A local clock, only to draw the sale countdown; the server decides when it ends. */
const nowMs = ref(Date.now());
let tick: ReturnType<typeof setInterval> | null = null;
onMounted(() => {
  tick = setInterval(() => (nowMs.value = Date.now()), 30_000);
  window.addEventListener('keydown', onKey);
});
onBeforeUnmount(() => {
  if (tick) clearInterval(tick);
  window.removeEventListener('keydown', onKey);
});

const endsIn = computed(() =>
  props.offer.endsAt ? boostRemaining(props.offer.endsAt, nowMs.value) : null,
);

const eyebrow = computed(() => {
  if (props.offer.discountPct) return 'Limited offer';
  return props.offer.kind === 'pack' ? 'Popular' : 'Featured';
});

function onKey(event: KeyboardEvent): void {
  if (event.key !== 'Escape') return;
  if (step.value === 'confirm') step.value = 'offer';
  else emit('close');
}

function confirmYes(): void {
  emit('buy', props.offer);
}
</script>

<template>
  <div class="scrim" data-test="special-offer" @click.self="emit('close')">
    <div class="dialog" :class="{ sale: !!offer.discountPct }" role="dialog" aria-modal="true" :aria-label="`Special offer: ${offer.title}`">
      <button type="button" class="close" aria-label="Close" data-action="offer-close" @click="emit('close')">×</button>

      <template v-if="step === 'offer'">
        <div class="art">
          <ItemPreview
            v-if="offer.kind === 'bundle'"
            :hull-id="offer.hullId ?? null"
            :turret-id="offer.turretId ?? null"
            framing="tank"
            :label="offer.line"
            eager
          />
          <img
            v-else-if="offer.image && !imageFailed"
            :src="offer.image"
            alt=""
            @error="imageFailed = true"
          />
          <span class="shine" aria-hidden="true"></span>
          <span v-if="offer.discountPct" class="flag" data-test="offer-discount">−{{ offer.discountPct }}%</span>
        </div>

        <p class="eyebrow">{{ eyebrow }}</p>
        <h2 class="title" data-test="offer-title">{{ offer.title }}</h2>
        <p class="line">{{ offer.line }}</p>

        <p class="price" data-test="offer-price">
          <component
            :is="'button'"
            type="button"
            class="plate buy"
            :data-action="offer.buyable ? 'offer-buy' : 'offer-price-view'"
            :disabled="offer.buyable ? busy || !canAfford : false"
            @click="offer.buyable ? (step = 'confirm') : emit('view', offer.section)"
          >
            <span class="plate-row">
              <template v-if="offer.kind === 'pack'">
                <strong>{{ formatPrice(offer.priceCents ?? 0, offer.currency ?? 'EUR') }}</strong>
                <s v-if="offer.fullPriceCents">{{ formatPrice(offer.fullPriceCents, offer.currency ?? 'EUR') }}</s>
              </template>
              <template v-else>
                <strong>{{ cost }} <CurrencyIcon kind="diamonds" /></strong>
                <s v-if="offer.fullDiamondCost">{{ offer.fullDiamondCost }} <CurrencyIcon kind="diamonds" :labelled="false" /></s>
              </template>
            </span>
            <span class="plate-cta">{{ !offer.buyable ? 'Open shop' : canAfford ? 'Buy now' : 'Not enough diamonds' }}</span>
          </component>
        </p>
        <p v-if="endsIn" class="ends" data-test="offer-ends">Ends in {{ endsIn }}</p>

        <div class="actions">
          <button
            type="button"
            class="btn"
            data-action="offer-view"
            @click="emit('view', offer.section)"
          >
            Open shop
            <svg class="cart" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M1.5 2.5H5l.6 2.5H22l-2.4 8.5H8.2l.5 2H20v2H7.1L3.6 4.5H1.5Z" />
              <circle cx="9" cy="20.5" r="1.8" />
              <circle cx="18" cy="20.5" r="1.8" />
            </svg>
          </button>
        </div>
        <button type="button" class="later" data-action="offer-later" @click="emit('close')">Maybe later</button>
      </template>

      <template v-else>
        <p class="eyebrow">Confirm</p>
        <h2 class="ask" data-test="offer-confirm-text">
          <span class="ask-lead">Are you sure you want to buy</span>
          <span class="ask-item">{{ offer.title }}</span>
          <span class="ask-price">for <strong>{{ cost }}</strong> <CurrencyIcon kind="diamonds" :labelled="false" /> diamonds?</span>
        </h2>
        <p class="line">{{ offer.line }}</p>
        <div class="actions">
          <button type="button" class="btn" data-action="offer-no" @click="step = 'offer'">No</button>
          <button
            type="button"
            class="btn primary yes"
            data-action="offer-yes"
            :disabled="busy"
            autofocus
            @click="confirmYes"
          >
            Yes — {{ cost }} <CurrencyIcon kind="diamonds" :labelled="false" />
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
.scrim {
  position: fixed;
  inset: 0;
  z-index: 95;
  display: grid;
  place-items: center;
  padding: 1rem;
  background: rgba(4, 6, 9, 0.72);
  backdrop-filter: blur(3px);
}

.dialog {
  position: relative;
  width: min(24rem, 100%);
  max-height: calc(100dvh - 2rem);
  overflow: auto;
  padding: 1rem 1.1rem 0.9rem;
  border: 1px solid var(--accent);
  border-radius: 5px;
  background:
    radial-gradient(120% 60% at 50% 0%, rgba(232, 114, 44, 0.3), transparent 62%),
    linear-gradient(180deg, rgba(14, 17, 22, 0.55), rgba(14, 17, 22, 0.8)),
    url('/assets/store/offer-card-bg.webp') center / cover,
    var(--steel-800);
  box-shadow:
    0 1.2rem 3rem rgba(0, 0, 0, 0.65),
    0 0 1.2rem rgba(232, 114, 44, 0.45),
    0 0 3.2rem rgba(232, 114, 44, 0.25);
  text-align: center;
  animation: glow 2.6s ease-in-out infinite, pop 380ms cubic-bezier(0.2, 1.2, 0.3, 1) both;
}

@keyframes glow {
  0%,
  100% {
    box-shadow:
      0 1.2rem 3rem rgba(0, 0, 0, 0.65),
      0 0 1rem rgba(232, 114, 44, 0.35),
      0 0 2.6rem rgba(232, 114, 44, 0.18);
  }
  50% {
    box-shadow:
      0 1.2rem 3rem rgba(0, 0, 0, 0.65),
      0 0 1.7rem rgba(255, 150, 70, 0.75),
      0 0 4.2rem rgba(232, 114, 44, 0.4);
  }
}

@keyframes pop {
  from {
    opacity: 0;
    transform: translateY(0.8rem) scale(0.94);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

.close {
  position: absolute;
  top: 0.35rem;
  right: 0.55rem;
  z-index: 2;
  width: 2.75rem;
  height: 2.75rem;
  border: 0;
  background: transparent;
  color: #fff;
  font-size: 1.6rem;
  line-height: 1;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.9);
  cursor: pointer;
}

.art {
  position: relative;
  aspect-ratio: 16 / 9;
  margin: 0 0 0.8rem;
  border: 1px solid var(--steel-700);
  border-radius: 4px;
  background: #11151a;
  overflow: hidden;
}

.art img,
.art :deep(img),
.art :deep(canvas) {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.flag {
  position: absolute;
  top: 0.5rem;
  left: 0.5rem;
  padding: 0.18rem 0.5rem;
  border: 1px solid var(--accent);
  border-radius: 3px;
  background: rgba(11, 13, 17, 0.85);
  color: var(--accent);
  font-weight: 700;
  letter-spacing: 0.06em;
}

.eyebrow {
  margin: 0;
  color: var(--accent);
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.title {
  margin: 0.15rem 0 0;
  font-size: 1.35rem;
  color: #fff;
}

.line {
  margin: 0.2rem 0 0;
  color: var(--steel-300, #aab4c0);
  font-size: 0.85rem;
}

.price {
  display: flex;
  justify-content: center;
  margin: 0.8rem 0 0;
}

/* The price on a bolted gold-edged plate, as a game store shows it: a small
   caption, the figure big and gold, the old price struck through beside it. */
.plate {
  position: relative;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 14rem;
  min-height: 4.2rem;
  padding: 0.6rem 2.6rem;
  background: url('/assets/store/offer-price-plate.webp') center / 100% 100% no-repeat;
  filter: drop-shadow(0 0 0.6rem rgba(255, 190, 70, 0.45));
  overflow: hidden;
  animation: plate-glow 2.4s ease-in-out infinite;
}

.plate.buy {
  border: 0;
  font: inherit;
  cursor: pointer;
}

.plate.buy:disabled {
  cursor: not-allowed;
  filter: grayscale(0.6) brightness(0.8);
  animation: none;
}

.plate-row {
  transition: transform 220ms cubic-bezier(0.2, 0.9, 0.3, 1);
}

.plate-cta {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0.75rem;
  background: linear-gradient(180deg, #fff1b0, #f2b93b 60%, #d58f12);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  opacity: 0;
  transform: translateY(0.4rem);
  transition: opacity 200ms, transform 220ms cubic-bezier(0.2, 0.9, 0.3, 1);
}

.plate.buy:hover:not(:disabled) .plate-row,
.plate.buy:focus-visible:not(:disabled) .plate-row {
  transform: translateY(-0.55rem) scale(0.72);
}

.plate.buy:hover .plate-cta,
.plate.buy:focus-visible .plate-cta,
.plate.buy:disabled .plate-cta {
  opacity: 1;
  transform: none;
}

.plate.buy:disabled .plate-row {
  transform: translateY(-0.55rem) scale(0.72);
}

.plate-row {
  display: inline-flex;
  align-items: baseline;
  gap: 0.6rem;
}

.plate strong {
  font-size: 1.6rem;
  line-height: 1.15;
  background: linear-gradient(180deg, #fff1b0, #f2b93b 60%, #d58f12);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  filter: drop-shadow(0 1px 0 rgba(0, 0, 0, 0.8));
}

.plate s {
  color: #8a8f98;
  font-size: 0.85rem;
  text-decoration-color: #e0452f;
}

@keyframes plate-glow {
  0%,
  100% {
    filter: drop-shadow(0 0 0.35rem rgba(255, 190, 70, 0.25));
  }
  50% {
    filter: drop-shadow(0 0 0.9rem rgba(255, 200, 90, 0.7));
  }
}

/* A soft light behind the picture and a slow sheen across it. */
.art {
  box-shadow: 0 0 1.2rem rgba(232, 114, 44, 0.35);
}

.shine {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(115deg, transparent 35%, rgba(255, 255, 255, 0.22) 50%, transparent 65%);
  background-size: 250% 100%;
  animation: shine 3.6s ease-in-out infinite;
}

@keyframes shine {
  from {
    background-position: 150% 0;
  }
  to {
    background-position: -100% 0;
  }
}

/* "Open shop": a cart that gives one quick shake every two seconds. */
.cart {
  display: inline-block;
  width: 1.2rem;
  height: 1.2rem;
  margin-left: 0.5rem;
  vertical-align: -0.2rem;
  fill: currentColor;
  transform-origin: 50% 90%;
  animation: cart-shake 2s infinite;
}

@keyframes cart-shake {
  0%,
  22%,
  100% {
    transform: none;
  }
  4% {
    transform: rotate(-14deg) translateX(-1px);
  }
  8% {
    transform: rotate(12deg) translateX(1px);
  }
  12% {
    transform: rotate(-8deg);
  }
  16% {
    transform: rotate(5deg);
  }
  20% {
    transform: rotate(-2deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .dialog,
  .plate,
  .shine,
  .cart {
    animation: none;
  }
}

.ends {
  margin: 0.15rem 0 0;
  color: var(--accent);
  font-size: 0.74rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.actions {
  display: flex;
  gap: 0.6rem;
  margin-top: 0.85rem;
}

.btn {
  flex: 1;
  min-height: 2.75rem;
  padding: 0.5rem 0.8rem;
  border: 1px solid var(--steel-500, #566170);
  border-radius: 3px;
  background: var(--steel-700, #2a323c);
  color: #fff;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}

.btn:hover:not(:disabled) {
  border-color: #fff;
}

.btn.primary {
  position: relative;
  overflow: hidden;
  border-color: #ffc489;
  /* A fill, not a gradient: style.css lays the steel texture over `background-color`. */
  background: #f07a30;
  color: #1a0c04;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.55),
    0 4px 0 #7a3410,
    0 0.6rem 1.4rem rgba(232, 114, 44, 0.55);
  transition:
    transform 0.12s ease,
    box-shadow 0.22s ease,
    filter 0.12s ease,
    border-color 0.12s ease;
}

/* A sheen crossing the face on a slow loop. */
.btn.primary::after {
  content: '';
  position: absolute;
  inset: 0 auto 0 -60%;
  width: 40%;
  background: linear-gradient(100deg, transparent, rgba(255, 255, 255, 0.55), transparent);
  transform: skewX(-18deg);
  animation: offer-sheen 3.6s ease-in-out infinite;
  pointer-events: none;
}

.btn.primary:hover:not(:disabled) {
  transform: translateY(-2px);
  border-color: #fff;
  color: #1a0c04;
  /* Halation: light bleeding off the lit edge, hot white to orange to a wide red fringe. */
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.6),
    inset 0 0 0.9rem rgba(255, 235, 200, 0.55),
    0 6px 0 #7a3410,
    0 0 0.6rem 0.1rem rgba(255, 220, 170, 0.85),
    0 0 1.6rem 0.4rem rgba(255, 140, 60, 0.7),
    0 0 3.4rem 1rem rgba(232, 80, 30, 0.45);
  text-shadow: 0 0 0.5rem rgba(255, 245, 220, 0.9);
}

.btn.primary:active:not(:disabled) {
  transform: translateY(2px);
  box-shadow:
    inset 0 2px 0 rgba(0, 0, 0, 0.3),
    0 1px 0 #7a3410;
}

@keyframes offer-sheen {
  0%,
  55% {
    left: -60%;
  }
  100% {
    left: 130%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .btn.primary::after {
    animation: none;
  }
}

.btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

/* The answer the dialog is steering toward: loud, and the one with focus. */
.btn.yes {
  font-size: 1.02rem;
}

.later {
  margin-top: 0.35rem;
  min-height: 2.75rem;
  padding: 0 1rem;
  border: 0;
  background: transparent;
  color: var(--steel-400);
  font: inherit;
  font-size: 0.78rem;
  cursor: pointer;
}

.later:hover {
  color: #fff;
}

.ask {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  margin: 0.4rem 0 0;
  font-family: var(--font-body);
  font-weight: 500;
  line-height: 1.35;
  color: #fff;
}

.ask-lead {
  font-size: 1rem;
  color: var(--steel-300, #aab4c0);
}

.ask-item {
  font-size: 1.3rem;
  font-weight: 700;
  text-decoration: underline;
  text-decoration-color: var(--accent);
  text-underline-offset: 0.25em;
}

.ask-price {
  font-size: 1.05rem;
}

.ask-price strong {
  color: var(--accent);
}
</style>
